import React, { useState, useEffect } from "react";
import { Loader, Button, Input, Dropdown, Icon, Table, Label } from "semantic-ui-react";
import { map, size } from "lodash";
import moment from "moment";
import { toast } from "react-toastify";
import { HeaderPage, TipsSettlement } from "../../components/Admin";
import { ModalConfirm } from "../../components/Common";
import { useCash, useAuth } from "../../hooks";
import { PAYMENT_METHODS } from "../../utils/constants";
import "./CashAdmin.scss";

const money = (value) => `S/ ${Number(value || 0).toFixed(2)}`;
const time = (value) => moment.parseZone(value).format("DD/MM/YYYY HH:mm");

const MOVEMENT_OPTIONS = [
  { key: "OUT", value: "OUT", text: "Egreso (sale efectivo)" },
  { key: "IN", value: "IN", text: "Ingreso (entra efectivo)" },
];

// Caja — "Apertura y cierre" (su "Cierre caja Z" en Restaurant.pe).
// Abrir con el efectivo inicial → registrar ingresos/egresos del turno →
// cerrar contando el efectivo: iCard muestra lo esperado y la diferencia.
// Solo el efectivo va al cajón; tarjeta, Yape y Plin se informan aparte.
export function CashAdmin() {
  const {
    loading,
    session,
    getCurrentCash,
    openCash,
    addCashMovement,
    voidCashMovement,
    closeCash,
    setSession,
  } = useCash();
  const { auth } = useAuth();
  const isAdmin = !!auth?.me?.is_staff;
  // Aviso antes de registrar un INGRESO cuyo motivo dice "venta": las ventas ya
  // se suman solas al efectivo esperado y registrarlas aquí las cuenta dos veces.
  const [showSaleWarning, setShowSaleWarning] = useState(false);
  // Movimiento que un Admin va a anular, y su motivo.
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [openingAmount, setOpeningAmount] = useState("");
  const [movement, setMovement] = useState({ kind: "OUT", amount: "", reason: "" });
  const [counted, setCounted] = useState("");
  const [notes, setNotes] = useState("");
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showZeroConfirm, setShowZeroConfirm] = useState(false);
  const [closedSession, setClosedSession] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getCurrentCash();
  }, []);

  const run = async (action, successMessage) => {
    setBusy(true);
    try {
      await action();
      if (successMessage) toast.success(successMessage);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const onOpen = () =>
    run(async () => {
      setShowZeroConfirm(false);
      await openCash(openingAmount);
      setOpeningAmount("");
      setClosedSession(null);
    }, "Caja abierta");

  // Abrir con S/ 0.00 es válido (cajón sin sencillo), pero tiene que ser una
  // decisión consciente: el campo vacío no abre, y el cero pide confirmación.
  const onOpenClick = () => {
    if (Number(openingAmount) === 0) setShowZeroConfirm(true);
    else onOpen();
  };

  const onAddMovement = () =>
    run(async () => {
      setShowSaleWarning(false);
      await addCashMovement(movement);
      setMovement({ kind: movement.kind, amount: "", reason: "" });
    }, "Movimiento registrado");

  const looksLikeSale = (text) => /\bventas?\b/i.test(text || "");
  const onRegisterClick = () => {
    if (movement.kind === "IN" && looksLikeSale(movement.reason)) setShowSaleWarning(true);
    else onAddMovement();
  };

  const onVoidMovement = () =>
    run(async () => {
      await voidCashMovement(voidTarget.id, voidReason.trim());
      setVoidTarget(null);
      setVoidReason("");
    }, "Movimiento anulado");

  const onClose = () =>
    run(async () => {
      setShowCloseConfirm(false);
      const result = await closeCash(counted, notes);
      setClosedSession(result);
      setCounted("");
      setNotes("");
      await getCurrentCash();
    }, "Caja cerrada");

  if (loading && !session && !closedSession) {
    return (
      <Loader active inline="centered">
        Cargando...
      </Loader>
    );
  }

  const summary = session?.summary;
  const difference =
    counted !== "" && summary ? Number(counted) - Number(summary.expected_cash) : null;

  return (
    <div className="cash-admin">
      <HeaderPage
        title="Caja — Apertura y cierre"
        btnTitle={
          <>
            <Icon name="refresh" /> Actualizar
          </>
        }
        btnClick={getCurrentCash}
      />

      {closedSession && <CloseResult session={closedSession} />}

      {!session ? (
        <div className="cash-admin__card cash-admin__open">
          <h3>La caja está cerrada</h3>
          <p className="cash-admin__muted">
            Ingresa el efectivo con el que empieza el turno (sencillo en el cajón).
          </p>
          <label>Monto inicial en efectivo (S/)</label>
          <Input
            type="number"
            min="0"
            step="0.10"
            placeholder="0.00"
            value={openingAmount}
            onChange={(_, data) => setOpeningAmount(data.value)}
          />
          <Button
            primary
            loading={busy}
            disabled={openingAmount === ""}
            onClick={onOpenClick}
          >
            <Icon name="unlock" /> Abrir caja
          </Button>
        </div>
      ) : (
        <>
          <p className="cash-admin__status">
            <Icon name="unlock" color="green" /> Caja abierta desde{" "}
            <strong>{time(session.opened_at)}</strong>
            {session.opened_by_name && ` por ${session.opened_by_name}`}
          </p>

          <div className="cash-admin__tiles">
            <Tile label="Monto inicial" value={money(session.opening_amount)} />
            {map(PAYMENT_METHODS, (method) => (
              <Tile
                key={method.value}
                label={`Ventas ${method.text}`}
                value={money(summary.totals[method.value])}
              />
            ))}
            {(Number(summary.tips_cash) > 0 || Number(summary.tips_card) > 0) && (
              <>
                <Tile label="Propinas en efectivo" value={money(summary.tips_cash)} />
                <Tile label="Propinas en tarjeta" value={money(summary.tips_card)} />
                {Number(summary.tips_paid_drawer) > 0 && (
                  <Tile label="Propinas pagadas (en egresos)" value={money(summary.tips_paid_drawer)} />
                )}
              </>
            )}
            <Tile label="Ingresos" value={money(summary.ins)} />
            <Tile label="Egresos" value={money(summary.outs)} />
            <Tile
              label="Efectivo esperado en caja"
              value={money(summary.expected_cash)}
              highlight
            >
              inicial + ventas en efectivo + propinas en efectivo + ingresos − egresos
            </Tile>
          </div>
          <p className="cash-admin__muted">
            {summary.sales_count} venta(s) cobrada(s) en este turno — total{" "}
            {money(summary.total_sales)}. Tarjeta, Yape y Plin no entran al cajón. Las propinas no son venta: se reparten en «Propinas» y salen del cajón como egresos.
          </p>

          <div className="cash-admin__grid">
            <div className="cash-admin__card">
              <h3>Ingresos/Egresos Varios</h3>
              <Dropdown
                selection
                fluid
                options={MOVEMENT_OPTIONS}
                value={movement.kind}
                onChange={(_, data) => setMovement({ ...movement, kind: data.value })}
              />
              <Input
                type="number"
                min="0"
                step="0.10"
                placeholder="Monto (S/)"
                value={movement.amount}
                onChange={(_, data) => setMovement({ ...movement, amount: data.value })}
              />
              <Input
                placeholder="Motivo (ej. compra de hielo)"
                value={movement.reason}
                onChange={(_, data) => setMovement({ ...movement, reason: data.value })}
              />
              <Button
                loading={busy}
                disabled={!movement.amount || !movement.reason.trim()}
                onClick={onRegisterClick}
              >
                Registrar
              </Button>

              {size(session.movements) > 0 && (
                <Table compact unstackable>
                  <Table.Body>
                    {map(session.movements, (m) => (
                      <Table.Row key={m.id} disabled={!!m.voided_at}>
                        <Table.Cell>
                          {moment.parseZone(m.created_at).format("HH:mm")}
                        </Table.Cell>
                        <Table.Cell>
                          <span style={m.voided_at ? { textDecoration: "line-through" } : undefined}>
                            {m.reason}
                          </span>
                          {m.voided_at && (
                            <div className="cash-admin__muted">
                              <Label size="mini" color="red">
                                Anulado
                              </Label>{" "}
                              {m.voided_by_name ? `por ${m.voided_by_name}: ` : ""}
                              {m.void_reason}
                            </div>
                          )}
                        </Table.Cell>
                        <Table.Cell
                          textAlign="right"
                          className={m.kind === "OUT" ? "cash-admin__out" : "cash-admin__in"}
                        >
                          <span style={m.voided_at ? { textDecoration: "line-through" } : undefined}>
                            {m.kind === "OUT" ? "−" : "+"} {money(m.amount)}
                          </span>
                          {isAdmin && !m.voided_at && !m.tip_settlement && (
                            <div>
                              <Button
                                basic
                                size="mini"
                                onClick={() => {
                                  setVoidTarget(m);
                                  setVoidReason("");
                                }}
                              >
                                Anular
                              </Button>
                            </div>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              )}
            </div>

            <TipsSettlement session={session} onSessionChange={setSession} />

            <div className="cash-admin__card">
              <h3>Cerrar caja</h3>
              <label>Efectivo contado en el cajón (S/)</label>
              <Input
                type="number"
                min="0"
                step="0.10"
                placeholder="0.00"
                value={counted}
                onChange={(_, data) => setCounted(data.value)}
              />
              {difference != null && (
                <p className={differenceClass(difference)}>
                  {differenceText(difference)}
                </p>
              )}
              <Input
                placeholder="Observaciones (opcional)"
                value={notes}
                onChange={(_, data) => setNotes(data.value)}
              />
              <Button
                negative
                loading={busy}
                disabled={counted === "" || summary.tips_pending_count > 0}
                onClick={() => setShowCloseConfirm(true)}
              >
                <Icon name="lock" /> Cerrar caja
              </Button>
              {summary.tips_pending_count > 0 && (
                <p className="cash-admin__diff cash-admin__diff--short">
                  Liquida las propinas antes de cerrar la caja.
                </p>
              )}
            </div>
          </div>
        </>
      )}

      <ModalConfirm
        title="Las ventas ya se suman solas"
        content={`El motivo dice "venta". Las ventas cobradas ya entran al efectivo esperado: si registras este ingreso, se contarían dos veces. Este apartado es para dinero que entra al cajón fuera de las ventas (por ejemplo, sencillo). ¿Registrarlo de todos modos?`}
        show={showSaleWarning}
        onCloseText="Corregir"
        onClose={() => setShowSaleWarning(false)}
        onConfirmText="Registrar de todos modos"
        onConfirm={onAddMovement}
      />

      <ModalConfirm
        title="¿Anular este movimiento?"
        content={
          <div>
            <p>
              {voidTarget &&
                `${voidTarget.kind === "OUT" ? "Egreso" : "Ingreso"} de ${money(voidTarget.amount)}: ${voidTarget.reason}. Deja de contar en el efectivo esperado y queda registrado quién lo anuló.`}
            </p>
            <Input
              fluid
              placeholder="Motivo de la anulación"
              maxLength={200}
              value={voidReason}
              onChange={(_, data) => setVoidReason(data.value)}
            />
          </div>
        }
        show={!!voidTarget}
        onCloseText="Cancelar"
        onClose={() => setVoidTarget(null)}
        onConfirmText="Anular movimiento"
        confirmDisabled={!voidReason.trim()}
        onConfirm={onVoidMovement}
      />

      <ModalConfirm
        title="¿Abrir la caja sin efectivo inicial?"
        content="Vas a abrir con S/ 0.00. Úsalo solo si el cajón empieza vacío; si hay sencillo, corrige el monto."
        show={showZeroConfirm}
        onCloseText="Corregir"
        onClose={() => setShowZeroConfirm(false)}
        onConfirmText="Abrir con S/ 0.00"
        onConfirm={onOpen}
      />

      <ModalConfirm
        title="¿Cerrar la caja?"
        content={
          difference != null
            ? `${differenceText(difference)}. El cierre queda registrado en "Cierres de caja".`
            : ""
        }
        show={showCloseConfirm}
        onCloseText="Cancelar"
        onClose={() => setShowCloseConfirm(false)}
        onConfirmText="Cerrar caja"
        onConfirm={onClose}
      />
    </div>
  );
}

export function differenceText(value) {
  const amount = Math.abs(value).toFixed(2);
  if (Math.abs(value) < 0.005) return "Cuadra exacto";
  return value > 0 ? `Sobrante de S/ ${amount}` : `Faltante de S/ ${amount}`;
}

export function differenceClass(value) {
  if (Math.abs(value) < 0.005) return "cash-admin__diff cash-admin__diff--ok";
  return value > 0
    ? "cash-admin__diff cash-admin__diff--over"
    : "cash-admin__diff cash-admin__diff--short";
}

function Tile(props) {
  const { label, value, children, highlight } = props;
  return (
    <div className={`cash-admin__tile${highlight ? " cash-admin__tile--highlight" : ""}`}>
      <p className="cash-admin__tile-label">{label}</p>
      <p className="cash-admin__tile-value">{value}</p>
      {children && <p className="cash-admin__muted">{children}</p>}
    </div>
  );
}

function CloseResult(props) {
  const { session } = props;
  const difference = Number(session.difference);
  return (
    <div className="cash-admin__card cash-admin__result">
      <h3>
        <Icon name="check circle" color="green" /> Caja cerrada —{" "}
        {time(session.closed_at)}
      </h3>
      <p>
        Esperado: <strong>{money(session.expected_amount)}</strong> · Contado:{" "}
        <strong>{money(session.counted_amount)}</strong>
      </p>
      <p className={differenceClass(difference)}>{differenceText(difference)}</p>
    </div>
  );
}
